import type * as FavCache from '@finn-no/favorite-api-browser-client';
import pulse from '@finn-no/pulse-sdk';
import { TrackerData } from '@finn-no/pulse-sdk/dist/types';
import { useRef } from 'react';
import { toast } from '@warp-ds/elements/toast';
import { i18n } from '@lingui/core';

type useCreateListProps = {
    api: FavCache.FavoriteBrowserCache;
    itemType: FavCache.ItemType;
    itemId: number;
    setErrorMessage: (errorMessage: string) => void;
    closeCallback: () => void;
};

const useCreateList = ({
    api,
    itemType,
    itemId,
    setErrorMessage,
    closeCallback,
}: useCreateListProps) => {
    const submittingRef = useRef(false);

    return async (folderName: string) => {
        // Just ignore additional submit events while processing the current request
        if (submittingRef.current) return;

        submittingRef.current = true;

        try {
            const res = await api.createFolder(folderName);
            if (res.status === 409) {
                const x = await res.json();
                throw x;
            }
            if (!res.ok) {
                throw res;
            }
            const { folderid: folderId } = await res.json();

            const pulsePayload: TrackerData = {
                type: 'Click',
                intent: 'Create',
                name: 'New favorite list',
                object: {
                    type: 'UIElement',
                    id: `favoritelist:${folderId}`,
                    elementType: 'Popup',
                },
                target: {
                    id: `favoritelist:${folderId}`,
                    type: 'SavedList',
                    name: 'Favorite list',
                },
            };
            pulse.trackEvent(pulsePayload);
            try {
                await api.addToFolder(itemType, itemId, folderId);

                const toastText = i18n.t({
                    id: 'favoriteAddedToast',
                    values: { folder: folderName },
                });
                toast(toastText, { type: 'success' });

                const pulsePayload: TrackerData = {
                    type: 'Click',
                    intent: 'Save',
                    name: 'New favorite',
                    object: {
                        id: `favoritelist:${folderId}`,
                        type: 'UIElement',
                        elementType: 'Popup',
                        name: 'Favorite List',
                        items: [
                            {
                                type: 'ClassifiedAd',
                                id:
                                    itemType === 'Hotel'
                                        ? `hotel:${itemId}`
                                        : `${itemId}`,
                                contentId: `${itemId}`,
                                name: 'n/a',
                                category: 'n/a',
                            },
                        ],
                    },
                    target: {
                        id:
                            itemType === 'Hotel'
                                ? `classified:hotel:${itemId}`
                                : `classified:${itemId}`,
                        type: 'SavedList',
                        name:
                            folderName === 'Mine julegaveønsker'
                                ? 'Mine julegaveønsker'
                                : 'Favorite list',
                    },
                };
                pulse.trackEvent(pulsePayload);
            } catch (error) {
                console.error(error);
                submittingRef.current = false;
                toast(
                    i18n.t({
                        id: 'favoriteAddedErrorToast',
                    }),
                    {
                        type: 'error',
                    },
                );
            } finally {
                closeCallback();
            }
        } catch (error: any) {
            console.error(error);
            submittingRef.current = false;
            try {
                if (!error.title) {
                    throw new TypeError();
                }
                setErrorMessage(error.title);
            } catch (error) {
                console.error(error);
                setErrorMessage(
                    i18n.t({
                        id: 'createListError',
                    }),
                );
            }
        }
    };
};

export default useCreateList;
